import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ExternalLink, ImageOff, X, ZoomIn } from "lucide-react";
import { loadLandDataAsset, type LandDataLevel, type LandDataLevelKey, type LandDataSource } from "@/lib/api";
import { useSession } from "@/lib/useAuth";
import { cn } from "@/lib/cn";

/* -------------------------------------------------------------------------- */
/* formatting                                                                  */
/* -------------------------------------------------------------------------- */

export function fmtNum(n: number | null | undefined, digits = 2): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("zh-CN", { maximumFractionDigits: digits });
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

export function fmtCoord(n: number | null | undefined): string {
  return n == null ? "—" : n.toFixed(5);
}

export function hostLabel(src: LandDataSource | null | undefined): string {
  if (!src) return "";
  return src.label || src.domain || src.url || "";
}

export function yearOf(v: number | string | null | undefined): number | null {
  const m = String(v ?? "").match(/(19|20)\d{2}/);
  return m ? Number(m[0]) : null;
}

/* -------------------------------------------------------------------------- */
/* evidence levels — one vocabulary for the whole page                         */
/* -------------------------------------------------------------------------- */

export const LEVEL_META: Record<
  LandDataLevelKey,
  { icon: string; label: string; cls: string; bar: string; hint: string }
> = {
  verified: {
    icon: "●",
    label: "已核验",
    cls: "text-good bg-good/10",
    bar: "bg-good",
    hint: "有页面链接和证据截图，或多个官方来源互证",
  },
  single: {
    icon: "◐",
    label: "单源",
    cls: "text-slate-600 bg-slate-100",
    bar: "bg-slate-400",
    hint: "第三方平台或单一来源，未交叉核验",
  },
  review: {
    icon: "▲",
    label: "需自看",
    cls: "text-warn bg-warn/10",
    bar: "bg-warn",
    hint: "多源冲突、卫星判读（下限）、撞显示上限或近似口径，请对照证据",
  },
  fallback: {
    icon: "↺",
    label: "往年",
    cls: "text-purple-600 bg-purple-50",
    bar: "bg-purple-400",
    hint: "该值年份早于同指标其他区县，比较时注意口径",
  },
  unpublished: {
    icon: "○",
    label: "查无公开",
    cls: "text-zinc-500 bg-zinc-100",
    bar: "bg-zinc-300",
    hint: "已核查，官方未公布该数值",
  },
  pending: {
    icon: "○",
    label: "待补",
    cls: "text-zinc-400 bg-white border border-dashed border-zinc-300",
    bar: "bg-white border border-dashed border-zinc-300",
    hint: "本次未取到",
  },
};

/** Full status pill — used inside the evidence panel only. */
export function LevelBadge({
  level,
  className,
}: {
  level: LandDataLevel | null | undefined;
  className?: string;
}): JSX.Element | null {
  if (!level) return null;
  const meta = LEVEL_META[level.level];
  if (!meta) return null;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
        meta.cls,
        className,
      )}
      title={level.note || meta.hint}
    >
      <span aria-hidden>{meta.icon}</span>
      {meta.label}
    </span>
  );
}

/**
 * On-page status mark. Usable values show nothing; only "needs checking" (▲) and
 * missing values (plain grey words) are surfaced, so the page reads as data first.
 */
export function Flag({
  level,
  withText = false,
  className,
}: {
  level: LandDataLevel | null | undefined;
  withText?: boolean;
  className?: string;
}): JSX.Element | null {
  if (!level) return null;
  if (level.level === "review") {
    return (
      <span
        className={cn("inline-flex shrink-0 items-center gap-0.5 text-[11px] text-warn", className)}
        title={`需对照证据自看：${level.note || LEVEL_META.review.hint}`}
      >
        ▲{withText ? <span>需核对</span> : null}
      </span>
    );
  }
  if (level.level === "pending" || level.level === "unpublished") {
    return (
      <span className={cn("shrink-0 text-[12px] text-zinc-400", className)} title={level.note}>
        {LEVEL_META[level.level].label}
      </span>
    );
  }
  return null;
}

export function ReadingHint({ yearTag = false }: { yearTag?: boolean }): JSX.Element {
  return (
    <p className="text-[11px] text-zinc-400">
      <span className="text-warn">▲</span> 需对照证据自看
      {yearTag ? (
        <>
          {" · "}
          <span className="rounded bg-zinc-100 px-1 text-zinc-500">2024</span> 灰色年份 = 与该列多数年份不同
        </>
      ) : null}
      {" · "}点数值看来源与截图
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/* lightbox — click any image to preview enlarged (never download)             */
/* -------------------------------------------------------------------------- */

interface LightboxCtx {
  open: (src: string, caption?: string) => void;
}
export const LightboxContext = createContext<LightboxCtx>({ open: () => {} });
export const useLightbox = (): LightboxCtx => useContext(LightboxContext);

export function Lightbox({
  shot,
  onClose,
}: {
  shot: { src: string; caption?: string } | null;
  onClose: () => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!shot) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [shot, onClose]);

  if (!shot) return null;
  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
        aria-label="关闭"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={shot.src}
        alt={shot.caption || "预览"}
        className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      {shot.caption ? (
        <div className="mt-3 max-w-2xl text-center text-[13px] text-white/80">{shot.caption}</div>
      ) : null}
    </div>
  );
}

/** Protected workspace image fetched with the bearer token, shown as an object URL. */
export function AssetImage({
  convId,
  asset,
  alt,
  className,
}: {
  convId: string;
  asset: string;
  alt: string;
  className?: string;
}): JSX.Element {
  const session = useSession();
  const lightbox = useLightbox();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setSrc(null);
    setFailed(false);
    if (!asset) {
      setFailed(true);
      return;
    }
    void loadLandDataAsset(session.token, convId, asset)
      .then((url) => alive && setSrc(url))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [session.token, convId, asset]);

  if (failed) {
    return (
      <div
        className={cn(
          "flex items-center justify-center gap-1.5 rounded-md border border-dashed border-cardline bg-canvas text-[12px] text-zinc-400",
          className,
        )}
      >
        <ImageOff className="h-3.5 w-3.5" />
        图片不可用
      </div>
    );
  }
  if (!src) {
    return <div className={cn("animate-pulse rounded-md bg-zinc-100", className)} />;
  }
  return (
    <button
      type="button"
      onClick={() => lightbox.open(src, alt)}
      className={cn(
        "group relative block overflow-hidden rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        className,
      )}
      title="点击放大预览"
    >
      <img src={src} alt={alt} className="h-full w-full rounded-md object-cover" loading="lazy" />
      <span className="pointer-events-none absolute right-1.5 top-1.5 rounded-md bg-black/45 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
        <ZoomIn className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

/** External evidence screenshot (OSS URL). Click → lightbox; a broken image says so. */
export function EvidenceShot({
  url,
  alt,
  className,
}: {
  url: string;
  alt: string;
  className?: string;
}): JSX.Element {
  const lightbox = useLightbox();
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="flex h-20 items-center justify-center gap-1.5 rounded-md border border-dashed border-cardline text-[12px] text-zinc-400 hover:text-brand-700"
      >
        <ImageOff className="h-3.5 w-3.5" />
        截图加载失败，点此打开原图
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => lightbox.open(url, alt)}
      title="点击放大预览证据截图"
      className={cn(
        "group relative block w-full overflow-hidden rounded-md border border-cardline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        className,
      )}
    >
      <img
        src={url}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="max-h-72 w-full object-cover object-top"
      />
      <span className="pointer-events-none absolute right-1.5 top-1.5 rounded-md bg-black/45 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
        <ZoomIn className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

/** Source rendered as a link chip (or plain text without a URL). */
export function SourceChip({
  src,
  short = false,
}: {
  src: LandDataSource | null | undefined;
  short?: boolean;
}): JSX.Element | null {
  const full = hostLabel(src);
  if (!full) return null;
  const label = short ? src?.domain || full : full;
  if (src?.url) {
    return (
      <a
        href={src.url}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full border border-cardline bg-white px-2 py-0.5 text-[11px] text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-50"
        title={`${full}\n${src.url}`}
      >
        <ExternalLink className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </a>
    );
  }
  return (
    <span
      className="inline-flex min-w-0 max-w-full items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-500"
      title={full}
    >
      <span className="truncate">{label}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* layout primitives                                                           */
/* -------------------------------------------------------------------------- */

export function SectionCard({
  id,
  icon,
  title,
  count,
  hint,
  actions,
  children,
}: {
  id?: string;
  icon: JSX.Element;
  title: string;
  count?: string;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section
      id={id}
      className="scroll-mt-4 overflow-hidden rounded-xl border border-cardline bg-white shadow-card"
    >
      <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-cardline bg-cardhead px-5 py-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-900 text-white">
          {icon}
        </span>
        <h2 className="text-[15px] font-semibold text-brand-900">{title}</h2>
        {count ? (
          <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-medium text-brand-700">
            {count}
          </span>
        ) : null}
        {hint ? <span className="text-[12px] text-zinc-400">{hint}</span> : null}
        {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

/** Neutral stat tile — numbers never carry good/bad colour on this page. */
export function StatTile({
  label,
  value,
  sub,
  level,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  level?: LandDataLevel | null;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-cardline bg-canvas px-4 py-3">
      <div className="flex items-center justify-between gap-2 text-[12px] text-zinc-500">
        <span>{label}</span>
        <Flag level={level} />
      </div>
      <div className="mt-1 text-[22px] font-semibold leading-tight tabular-nums text-zinc-900">{value}</div>
      {sub ? <div className="mt-0.5 text-[11px] leading-4 text-zinc-400">{sub}</div> : null}
    </div>
  );
}
